/** @jsxImportSource @emotion/react */
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import { Backdrop, CircularProgress, Dialog, IconButton } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState, VFC } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { CountryPath } from "src/entities/CountryPath";
import { PricesResponse } from "src/entities/v3/PricesResponse";
import { Auth } from "src/hooks/useAuth";
import { useFetch } from "src/hooks/useFetch";
import { NumberUtils } from "src/utils/NumberUtils";
import getCountryName from "src/utils/getCountryName";
import Button from "../components/common/Button";
import { LightTooltip } from "../components/common/LightTooltip";
import PageHeader from "../components/page/PageHeader";
import PageWrapper from "../components/page/PageWrapper";

interface Props {
  // eslint-disable-next-line react/no-unused-prop-types
  auth: Auth;
  country: CountryPath;
  isPublisherInfoLoading: boolean;
  credits?: string;
  is_subscripted: boolean;
  defaultCountry?: string;
  remaining_quota?: number;
  quota_reset_day?: number;
}

const CreditsmanagementPage: VFC<Props> = (props: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Common parameters
   */
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Buying Credits
   */
  const [buyingCredits, setBuyingCredits] = useState(1);
  /**
   * Increment buying credits
   */
  const incrementBuyingCredits = () => {
    setBuyingCredits(buyingCredits + 1);
  };
  /**
   * Decrement buying credits
   */
  const decrementBuyingCredits = () => {
    if (buyingCredits > 1) {
      setBuyingCredits(buyingCredits - 1);
    }
  };

  /**
   * Post redirect credit purchase session
   */
  const redirectPurchaseCredits = async () => {
    handleBackdropOpen();
    await axios
      .request({
        method: "POST",
        url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/payment/payment_session`,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          authorization: `Bearer ${props.auth.authInfo.accessToken}`,
          country: props.country,
        },
        data: {
          successPath: `credits_management?result=added_success&country=${props.country}`,
          cancelPath: `credits_management?country=${props.country}`,
          quantity: buyingCredits,
          country: props.country,
        },
      })
      .then((res) => {
        /**
         *
         * Open tab
         */
        const { url } = res.data;
        window.location.href = url;
        // const win = window.open(url, "_blank");
        // win?.focus();
      })
      .catch(() => {
        handleBackdropClose();
        enqueueSnackbar(t("error.login_again"), {
          variant: "error",
        });
      });
  };

  /**
   * Post redirect subscription purchase session
   */
  const redirectPurchaseSubscription = async () => {
    handleBackdropOpen();
    await axios
      .request({
        method: "POST",
        url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/payment/subscription_session`,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          authorization: `Bearer ${props.auth.authInfo.accessToken}`,
          country: props.country,
        },
        data: {
          successPath: `credits_management?result=added_success&country=${props.country}`,
          cancelPath: `credits_management?country=${props.country}`,
          country: props.country,
        },
      })
      .then((res) => {
        /**
         * Open tab
         */
        const { url } = res.data;
        window.location.href = url;
        // const win = window.open(url, "_blank");
        // win?.focus();
      })
      .catch(() => {
        handleBackdropClose();
        enqueueSnackbar(t("error.login_again"), {
          variant: "error",
        });
      });
  };

  /**
   * Post redirect customer portal session
   */
  const redirectCustomerPortal = async () => {
    handleBackdropOpen();
    await axios
      .request({
        method: "POST",
        url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/payment/portal_session`,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          authorization: `Bearer ${props.auth.authInfo.accessToken}`,
          country: props.country,
        },
        data: {
          returnPath: `credits_management?country=${props.country}`,
        },
      })
      .then((res) => {
        handleBackdropClose();
        /**
         * Open tab
         */
        const { url } = res.data;
        window.location.href = url;
        // const win = window.open(url, "_blank");
        // win?.focus();
      })
      .catch(() => {
        handleBackdropClose();
        enqueueSnackbar(t("error.login_again"), {
          variant: "error",
        });
      });
  };

  /**
   * Get credit meta info
   */
  const { data: metaPrice, isLoading: isMetaPriceLoading } =
    useFetch<PricesResponse>({
      url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/payment/prices?country=${props.country}`,
      skip: props.credits === undefined,
    });

  /**
   * Dropdown settings
   */
  /**
   * Dropdown parameters
   */
  const [backdropOpen, setBackdropOpen] = useState(false);
  /**
   * Open backdrop
   */
  const handleBackdropOpen = () => {
    setBackdropOpen(true);
  };
  /**
   * Close backdrop
   */
  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  /**
   * modal parameters
   */
  const [modalOpen, setModalOpen] = useState(false);
  /**
   * Modal open
   */
  const handleModalOpen = () => {
    setModalOpen(true);
  };
  /**
   * modal close
   */
  const handleModalClose = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);
    const result = params.get("result");

    if (result === "added_success") {
      enqueueSnackbar(t("credits_management.purchase.success"), {
        variant: "success",
      });
      const param = searchParams.get("result");
      if (param) {
        searchParams.delete("result");
        setSearchParams(searchParams);
      }
    }
    if (result === "subscription_success") {
      enqueueSnackbar(t("credits_management.subscribe.success"), {
        variant: "success",
      });
      const param = searchParams.get("result");
      if (param) {
        searchParams.delete("result");
        setSearchParams(searchParams);
      }
    }
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <PageWrapper>
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="md"
        >
          <div className="p-8">
            <div style={{ textAlign: "right" }}>
              <IconButton onClick={handleModalClose}>
                <CloseIcon />
              </IconButton>
            </div>
            {isMetaPriceLoading ? (
              <CircularProgress color="inherit" />
            ) : (
              <div>
                <div className="flex items-center">
                  <div className="border w-64 rounded-lg">
                    <div className="rounded-t-lg flex justify-center px-4 py-2 bg-primary text-white font-bold text-md">
                      {t("credits_management.pay_as_you_go")}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center mb-4">
                        <button
                          className="border h-6 w-6 rounded flex items-center justify-center"
                          type="button"
                          onClick={decrementBuyingCredits}
                        >
                          <RemoveIcon style={{ width: 12 }} />
                        </button>
                        <div className="font-bold text-primary text-3xl mx-2">
                          {buyingCredits}
                        </div>
                        <button
                          className="border h-6 w-6 rounded flex items-center justify-center"
                          type="button"
                          onClick={incrementBuyingCredits}
                        >
                          <AddIcon style={{ width: 12 }} />
                        </button>
                        <div className="ml-2 font-bold">
                          {t("payment.press_post.credit")}
                        </div>
                      </div>
                      <div className="flex mb-4 items-end">
                        <div className="text text-base font-bold mr-4">
                          {t("general.total")}
                        </div>
                        <div className="text-primary text-xl font-bold">
                          {metaPrice &&
                            NumberUtils.getCurrency(metaPrice.currency)}
                          {metaPrice &&
                            NumberUtils.numberToFinancialDisplayForIndonesia(
                              buyingCredits * metaPrice.creditPrice
                            )}
                        </div>
                      </div>
                      <Button
                        text={t("general.buy")}
                        className="mb-4"
                        isFullWidth
                        disabled={buyingCredits === 0}
                        onClick={redirectPurchaseCredits}
                        openInNewTab
                      />
                      <div className="flex">
                        <CheckCircleOutlineIcon
                          fontSize="small"
                          className="text-blue-500"
                        />
                        <div>{t("credits_management.cost")}</div>
                      </div>
                    </div>
                  </div>
                  {props.country !== "USA" && (
                    <div className="mx-4">{t("credits_management.cost2")}</div>
                  )}
                  {/* サブスクリプションカード: USAの場合は非表示 */}
                  {props.country !== "USA" && (
                    <div className="border w-64 rounded-lg">
                      <div className="rounded-t-lg flex justify-center px-4 py-2 bg-primary text-white font-bold text-md">
                        {t("general.subscribe")}
                        <LightTooltip
                          placement="top"
                          title={t("credits_management.cost3")}
                        >
                          <LightbulbOutlinedIcon fontSize="small" />
                        </LightTooltip>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center mb-4 h-9">
                          <div className="text-primary font-bold text-2xl">
                            {t("credits_management.basic")}
                          </div>
                          <div className="ml-2 font-bold">
                            {t("credits_management.subscription")}
                          </div>
                        </div>
                        <div className="flex mb-4 items-end">
                          <div className="text text-base font-bold mr-4">
                            {t("credits_management.monthly")}
                          </div>
                          <div className="text-primary text-xl font-bold">
                            {metaPrice &&
                              NumberUtils.getCurrency(metaPrice.currency)}
                            {metaPrice &&
                              NumberUtils.numberToFinancialDisplayForIndonesia(
                                metaPrice.subscriptionPrice
                              )}
                          </div>
                        </div>
                        <Button
                          text={t("credits_management.subscribe")}
                          className="mb-4"
                          isFullWidth
                          openInNewTab
                          onClick={redirectPurchaseSubscription}
                        />
                        <div className="flex">
                          <CheckCircleOutlineIcon
                            fontSize="small"
                            className="text-blue-500"
                          />
                          <div>
                            {t("credits_management.unlimited_plan.maximum")}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {props.defaultCountry !== "JPN" && (
                  <div className="mt-2 whitespace-pre">
                    {t("credits_management.contact")}
                    <a
                      target="_blank"
                      href="https://wa.me/+6285119772155"
                      rel="noreferrer"
                    >
                      +62 85119772155
                    </a>
                    {"\nor marketing@vritimes.com"}.
                  </div>
                )}
              </div>
            )}
          </div>
        </Dialog>

        <Backdrop sx={{ color: "#fff", zIndex: 10001 }} open={backdropOpen}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <PageHeader title={t("credits_management.page_header")} />
        {props.isPublisherInfoLoading ? (
          <div className="flex h-full w-full justify-center">
            <CircularProgress />
          </div>
        ) : (
          <div>
            <div>
              <div className="mb-4 text font-bold text-lg flex items-center">
                {`${t("credits_management.current_plan")}: ${
                  props.is_subscripted
                    ? t("credits_management.subscription")
                    : t("credits_management.pay_as_you_go")
                }`}
                {props.is_subscripted && (
                  <div className="ml-2">
                    <LightTooltip
                      placement="right"
                      title={t("credits_management.balance.tooltip", {
                        credits: props.credits,
                      })}
                    >
                      <TipsAndUpdatesOutlinedIcon />
                    </LightTooltip>
                  </div>
                )}
              </div>
              <div className="p-4 border rounded-lg min-w-64 w-fit mb-8">
                <div className="mb-4">
                  <div className="flex items-end mb-2">
                    {props.is_subscripted ? (
                      <div className="font-bold text-primary text-3xl">
                        {t("credits_management.subscribed")}
                      </div>
                    ) : (
                      <>
                        <div className="text font-bold text-lg">
                          {getCountryName(props.country)} Credits
                        </div>
                        <div className="font-bold text-primary text-3xl ml-2">
                          {props.credits}
                        </div>
                      </>
                    )}
                  </div>
                  {props.is_subscripted &&
                    props.remaining_quota !== undefined && (
                      <div>
                        <div className="text-sm text-gray-600 mb-1 whitespace-nowrap">
                          {t("credits_management.remaining_posts_this_month")}:{" "}
                          {props.remaining_quota}
                        </div>
                        {props.quota_reset_day !== undefined && (
                          <div className="text-sm text-gray-600 mb-1 whitespace-nowrap">
                            {t("credits_management.quota_reset_day")}:{" "}
                            {props.quota_reset_day}
                            {t("credits_management.day_suffix")}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 whitespace-nowrap">
                          {t("credits_management.additional_posts_contact")}{" "}
                          <a
                            target="_blank"
                            href="https://wa.me/+6285119772155"
                            rel="noreferrer"
                            className="underline hover:text-gray-700"
                          >
                            +62 85119772155
                          </a>
                          {" or marketing@vritimes.com"}
                        </div>
                      </div>
                    )}
                </div>
                <div className="flex justify-end">
                  {/* サブスクボタン: USAの場合は非表示 */}
                  {props.is_subscripted ? (
                    props.country !== "USA" && (
                      <Button
                        text={t("credits_management.detail")}
                        variant="outlined"
                        openInNewTab
                        onClick={redirectCustomerPortal}
                      />
                    )
                  ) : (
                    <Button
                      text={t("credits_management.add")}
                      id="payment_modal_button"
                      variant="outlined"
                      onClick={() => {
                        setBuyingCredits(1);
                        handleModalOpen();
                      }}
                    />
                  )}
                </div>
              </div>

              <div className="mb-12">
                <div className="text-xl font-bold mb-2">
                  {t("credits_management.billing.title")}
                </div>
                <div>
                  <div className="mb-1">
                    {t("credits_management.billing.description")}
                  </div>
                  <Button
                    text={t("general.setting")}
                    variant="outlined"
                    openInNewTab
                    onClick={redirectCustomerPortal}
                  />
                </div>
              </div>

              {/* Price information section - conditionally rendered based on country */}
              {props.country === "IDN" || props.country === "PHL" ? (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-6">
                    {props.country === "IDN" ? "Indonesia" : "Philippines"}{" "}
                    Pricing Plans
                  </h2>

                  {/* Pay As You Go Table */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                    <div className="p-6 pb-0">
                      <h3 className="text-xl font-semibold mb-4">
                        Pay As You Go
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left p-4 font-semibold border-b border-r">
                              No Subscription Required
                            </th>
                            {props.country === "IDN" ? (
                              <>
                                <th className="text-center p-4 font-semibold border-b border-r">
                                  <div>Basic</div>
                                  <div className="text-sm text-gray-600 font-normal mt-1">
                                    <span className="text-blue-600 font-bold">
                                      100
                                    </span>{" "}
                                    Media Guaranteed
                                  </div>
                                </th>
                                <th className="text-center p-4 font-semibold border-b border-r">
                                  <div>Standard</div>
                                  <div className="text-sm text-gray-600 font-normal mt-1">
                                    <span className="text-blue-600 font-bold">
                                      150
                                    </span>{" "}
                                    Media Guaranteed
                                  </div>
                                </th>
                                <th className="text-center p-4 font-semibold border-b">
                                  <div>Premium</div>
                                  <div className="text-sm text-gray-600 font-normal mt-1">
                                    <span className="text-blue-600 font-bold">
                                      200
                                    </span>{" "}
                                    Media Guaranteed
                                  </div>
                                </th>
                              </>
                            ) : (
                              <th className="text-center p-4 font-semibold border-b">
                                <div>Basic</div>
                                <div className="text-sm text-gray-600 font-normal mt-1">
                                  <span className="text-blue-600 font-bold">
                                    50
                                  </span>{" "}
                                  Media Guaranteed
                                </div>
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="p-4 font-medium border-r">
                              1 Credit ={" "}
                              <span className="text-blue-600 font-bold">
                                {props.country === "IDN"
                                  ? "Rp499,000"
                                  : "₱1,790"}
                              </span>
                            </td>
                            {props.country === "IDN" ? (
                              <>
                                <td className="text-center p-4 border-r">
                                  <div className="text-sm font-medium text-gray-600 mb-1">
                                    1 Credit for 1 Release
                                  </div>
                                  <div className="text-xl font-bold">
                                    Rp499,000
                                  </div>
                                </td>
                                <td className="text-center p-4 border-r">
                                  <div className="text-sm font-medium text-gray-600 mb-1">
                                    2 Credits for 1 Release
                                  </div>
                                  <div className="text-xl font-bold">
                                    Rp998,000
                                  </div>
                                </td>
                                <td className="text-center p-4">
                                  <div className="text-sm font-medium text-gray-600 mb-1">
                                    3 Credits for 1 Release
                                  </div>
                                  <div className="text-xl font-bold">
                                    Rp1,497,000
                                  </div>
                                </td>
                              </>
                            ) : (
                              <td className="text-center p-4">
                                <div className="text-sm font-medium text-gray-600 mb-1">
                                  1 Credit for 1 Release
                                </div>
                                <div className="text-xl font-bold">₱1,790</div>
                              </td>
                            )}
                          </tr>
                          {/* 3X Bundle (20% off) */}
                          <tr className="border-t bg-green-50">
                            <td className="p-4 font-medium border-r">
                              3X Bundle{" "}
                              <span className="text-green-600 font-bold">
                                20% OFF
                              </span>
                              <div className="text-sm font-medium text-gray-600 mt-1">
                                3 Credits for 3 Releases
                              </div>
                            </td>
                            {props.country === "IDN" ? (
                              <td className="text-center p-4" colSpan={3}>
                                <span className="text-gray-400 line-through mr-2">
                                  Rp1,497,000
                                </span>
                                <span className="text-xl font-bold text-green-600">
                                  Rp1,190,000
                                </span>
                              </td>
                            ) : (
                              <td className="text-center p-4">
                                <span className="text-gray-400 line-through mr-2">
                                  ₱5,370
                                </span>
                                <span className="text-xl font-bold text-green-600">
                                  ₱4,290
                                </span>
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="px-6 pb-6 pt-2 text-sm text-gray-600">
                      {props.country === "IDN"
                        ? "The Standard, Premium, and 3X Bundle plans cannot be purchased directly via Stripe. "
                        : "The 3X Bundle cannot be purchased directly via Stripe. "}
                      Please contact us at{" "}
                      <a
                        target="_blank"
                        href="https://wa.me/+6285119772155"
                        rel="noreferrer"
                        className="text-green-600 underline hover:text-green-700"
                      >
                        +62 85119772155
                      </a>{" "}
                      or{" "}
                      <a
                        href="mailto:marketing@vritimes.com"
                        className="text-green-600 underline hover:text-green-700"
                      >
                        marketing@vritimes.com
                      </a>{" "}
                      to purchase.
                    </div>
                  </div>

                  {/* OR Divider */}
                  <div className="flex items-center justify-center my-8">
                    <div className="flex-1 border-t border-gray-300" />
                    <div className="mx-4 px-4 py-2 bg-gray-100 rounded-full">
                      <span className="text-lg font-semibold text-gray-600">
                        OR
                      </span>
                    </div>
                    <div className="flex-1 border-t border-gray-300" />
                  </div>

                  {/* Subscription Plans Table */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 pb-0">
                      <h3 className="text-xl font-semibold mb-4">
                        Subscription Plans
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left p-4 font-semibold border-b border-r">
                              {props.country === "IDN" ? (
                                <>
                                  <div>Monthly Subscription</div>
                                  <div className="text-xs text-gray-600 font-normal mt-2">
                                    <div>Up to 1 press release per day only.</div>
                                    <div>
                                      Subscription is valid for 30 calendar
                                      days, unused releases will expire.
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <div className="text-xs text-gray-600 font-normal mt-2">
                                  <div>Up to 1 press release per day only.</div>
                                  <div>
                                    Subscription renews unless cancelled before
                                    renewal date. Credits are valid during the
                                    subscription period only, unused credits
                                    will expire.
                                  </div>
                                </div>
                              )}
                            </th>
                            {props.country === "IDN" ? (
                              <>
                                <th className="text-center p-4 font-semibold border-b border-r">
                                  <div>Basic</div>
                                  <div className="text-sm text-gray-600 font-normal mt-1">
                                    <span className="text-blue-600 font-bold">
                                      100
                                    </span>{" "}
                                    Media Guaranteed
                                  </div>
                                </th>
                                <th className="text-center p-4 font-semibold border-b border-r">
                                  <div>Standard</div>
                                  <div className="text-sm text-gray-600 font-normal mt-1">
                                    <span className="text-blue-600 font-bold">
                                      150
                                    </span>{" "}
                                    Media Guaranteed
                                  </div>
                                </th>
                                <th className="text-center p-4 font-semibold border-b">
                                  <div>Premium</div>
                                  <div className="text-sm text-gray-600 font-normal mt-1">
                                    <span className="text-blue-600 font-bold">
                                      200
                                    </span>{" "}
                                    Media Guaranteed
                                  </div>
                                </th>
                              </>
                            ) : (
                              <th className="text-center p-4 font-semibold border-b">
                                <div>Basic</div>
                                <div className="text-sm text-gray-600 font-normal mt-1">
                                  <span className="text-blue-600 font-bold">
                                    50
                                  </span>{" "}
                                  Media Guaranteed
                                </div>
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-4 font-medium border-r">
                              {props.country === "IDN" ? (
                                <>
                                  <span className="text-blue-600 font-bold">
                                    Max 10
                                  </span>{" "}
                                  Press Releases
                                </>
                              ) : (
                                <>
                                  <span className="text-blue-600 font-bold">
                                    Monthly Subscription:
                                  </span>{" "}
                                  Max 10 press releases within 30 days
                                </>
                              )}
                            </td>
                            {props.country === "IDN" ? (
                              <>
                                <td className="text-center p-4 border-r">
                                  <div className="text-xl font-bold">
                                    Rp1,990,000
                                  </div>
                                </td>
                                <td className="text-center p-4 border-r">
                                  <div className="text-xl font-bold">
                                    Rp3,980,000
                                  </div>
                                </td>
                                <td className="text-center p-4">
                                  <div className="text-xl font-bold">
                                    Rp5,970,000
                                  </div>
                                </td>
                              </>
                            ) : (
                              <td className="text-center p-4">
                                <div className="text-xl font-bold">₱6,990</div>
                              </td>
                            )}
                          </tr>
                          <tr>
                            <td className="p-4 font-medium border-r">
                              {props.country === "IDN" ? (
                                <>
                                  <span className="text-blue-600 font-bold">
                                    Max 30
                                  </span>{" "}
                                  Press Releases
                                </>
                              ) : (
                                <>
                                  <span className="text-blue-600 font-bold">
                                    Yearly Subscription:
                                  </span>{" "}
                                  Max 30 press releases within 365 days
                                </>
                              )}
                            </td>
                            {props.country === "IDN" ? (
                              <>
                                <td className="text-center p-4 border-r">
                                  <div className="text-xl font-bold">
                                    Rp3,980,000
                                  </div>
                                </td>
                                <td className="text-center p-4 border-r">
                                  <div className="text-xl font-bold">
                                    Rp7,960,000
                                  </div>
                                </td>
                                <td className="text-center p-4">
                                  <div className="text-xl font-bold">
                                    Rp11,940,000
                                  </div>
                                </td>
                              </>
                            ) : (
                              <td className="text-center p-4">
                                <div className="text-xl font-bold">₱21,990</div>
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : props.country === "USA" ? (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-6">
                    United States Pricing Plans
                  </h2>

                  {/* Pay As You Go Table */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                    <div className="p-6 pb-0">
                      <h3 className="text-xl font-semibold mb-4">
                        Pay As You Go
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left p-4 font-semibold border-b border-r">
                              No Subscription Required
                            </th>
                            <th className="text-center p-4 font-semibold border-b border-r">
                              <div>Basic</div>
                              <div className="text-sm text-gray-600 font-normal mt-1">
                                <span className="text-blue-600 font-bold">
                                  400
                                </span>{" "}
                                Media Guaranteed
                              </div>
                            </th>
                            <th className="text-center p-4 font-semibold border-b">
                              <div>Premium</div>
                              <div className="text-sm text-gray-600 font-normal mt-1">
                                <span className="text-blue-600 font-bold">
                                  400
                                </span>{" "}
                                Media Guaranteed
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="p-4 font-medium border-r">
                              1 Credit ={" "}
                              <span className="text-blue-600 font-bold">
                                $99
                              </span>
                            </td>
                            <td className="text-center p-4 border-r">
                              <div className="text-sm font-medium text-gray-600 mb-1">
                                1 Credit for 1 Release
                              </div>
                              <div className="text-xl font-bold">$99</div>
                            </td>
                            <td className="text-center p-4">
                              <div className="text-sm font-medium text-gray-600 mb-1">
                                3 Credits for 1 Release
                              </div>
                              <div className="text-xl font-bold">$297</div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="p-6 rounded-lg"
                  style={{
                    background: "#DCE5FE",
                    width: "fit-content",
                  }}
                >
                  <div className="text-black text-lg font-semibold mb-2">
                    Price for {getCountryName(props.country)}
                  </div>
                  <div className="flex justify-start items-center">
                    <div className="border w-72 h-48 bg-white rounded-lg">
                      <div className="flex justify-center bg-primary text-white py-4 font-semibold text-sm rounded-t-lg">
                        {t("credits_management.pay_as_you_go")}
                      </div>
                      <div className="flex flex-col items-center pt-8 px-6">
                        <div className="font-semibold">
                          {t("credits_management.credit_eq_press_release")}
                        </div>
                        <div className="text-black font-semibold text-lg mb-4">
                          {metaPrice &&
                            NumberUtils.getCurrency(metaPrice.currency)}
                          {metaPrice &&
                            NumberUtils.numberToFinancialDisplayForIndonesia(
                              metaPrice.creditPrice
                            )}
                        </div>
                        <div className="flex items-center pb-6 text-xs">
                          <CheckCircleOutlineIcon
                            className="mr-1"
                            fontSize="small"
                            style={{ color: "#3B82F6" }}
                          />
                          {t("credits_management.no_subscription_required")}
                        </div>
                      </div>
                    </div>
                    <div className="font-semibold mx-4">or</div>
                    <div className="border w-72 bg-white rounded-lg">
                      <div className="flex justify-center bg-primary text-white py-4 font-semibold text-sm rounded-t-lg">
                        {t("credits_management.subscription")}
                      </div>
                      <div className="flex flex-col items-center pt-8 px-6">
                        <div className="font-semibold">
                          {t("credits_management.basic_plan")}
                        </div>
                        <div className="text-black font-semibold text-lg mb-4">
                          {metaPrice &&
                            NumberUtils.getCurrency(metaPrice.currency)}
                          {metaPrice &&
                            NumberUtils.numberToFinancialDisplayForIndonesia(
                              metaPrice.subscriptionPrice
                            )}
                          <span className="text-sm ml-1">/month</span>
                        </div>
                        <div className="pb-6 text-xs">
                          <div className="flex items-center mb-1">
                            <CheckCircleOutlineIcon
                              className="mr-1"
                              fontSize="small"
                              style={{ color: "#3B82F6" }}
                            />
                            {t("credits_management.unlimited_plan.maximum")}
                          </div>
                          <div className="flex items-center mb-1">
                            <CheckCircleOutlineIcon
                              className="mr-1"
                              fontSize="small"
                              style={{ color: "#3B82F6" }}
                            />
                            {t("credits_management.unlimited_plan.31st")}
                          </div>
                          <div className="flex items-center mb-1">
                            <CheckCircleOutlineIcon
                              className="mr-1"
                              fontSize="small"
                              style={{ color: "#3B82F6" }}
                            />
                            {t("credits_management.unlimited_plan.one_day")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {props.country === "PHL" && (
                <div
                  className="p-6 rounded-lg mt-4"
                  style={{
                    background: "#DCE5FE",
                    width: "fit-content",
                  }}
                >
                  <div className="text-black text-lg font-semibold mb-2">
                    Premium Package
                  </div>
                  <div className="flex justify-start items-center">
                    <div className="border w-72 bg-white rounded-lg mr-12">
                      <div className="flex justify-center bg-primary text-white py-4 font-semibold text-sm rounded-t-lg">
                        Top Media Guarantee
                      </div>
                      <div className="flex flex-col items-center pt-8 px-6">
                        <div className="font-semibold">
                          1 Press Release Starting from
                        </div>
                        <div className="text-black font-semibold text-lg mb-4">
                          ₱ 40,000
                        </div>
                        <Button
                          text="Get a quote"
                          className="mb-4"
                          isFullWidth
                          onClick={() => {
                            window.open(
                              "https://docs.google.com/forms/d/e/1FAIpQLScdaRAxO-cKD4B13jyiTkGLT5t87lLCfJm_Ozv5qkXcODEpTQ/viewform",
                              "_blank"
                            );
                          }}
                          openInNewTab
                        />
                        <div className="text-xs text-left space-y-1 pb-4">
                          <div className="flex items-start">
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              className="text-blue-500 mr-1"
                              style={{ fontSize: "14px" }}
                            />
                            <span>
                              Guaranteed online media feature of your choice
                            </span>
                          </div>
                          <div className="flex items-start">
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              className="text-blue-500 mr-1"
                              style={{ fontSize: "14px" }}
                            />
                            <span>Permanent publication</span>
                          </div>
                          <div className="flex items-start">
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              className="text-blue-500 mr-1"
                              style={{ fontSize: "14px" }}
                            />
                            <span>Do-follow links</span>
                          </div>
                          <div className="flex items-start">
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              className="text-blue-500 mr-1"
                              style={{ fontSize: "14px" }}
                            />
                            <span>Up to 400 words</span>
                          </div>
                          <div className="flex items-start">
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              className="text-blue-500 mr-1"
                              style={{ fontSize: "14px" }}
                            />
                            <span>Up to 2 pictures</span>
                          </div>
                          <div className="flex items-start">
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              className="text-blue-500 mr-1"
                              style={{ fontSize: "14px" }}
                            />
                            <span>Below market-rate</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </PageWrapper>
    </LocalizationProvider>
  );
};

export default CreditsmanagementPage;
